import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import { useOpenChangeComplete } from '../hooks/use-open-change-complete';
import {
  dialogCloseIconClasses,
  dialogContentClasses,
  dialogContentPositionAbsoluteClasses,
  dialogContentPositionFixedClasses,
  dialogDescriptionClasses,
  dialogFooterClasses,
  dialogHeaderClasses,
  dialogOverlayClasses,
  dialogTitleClasses,
} from './dialog.styles';
import type {
  DialogContentProps,
  DialogDescriptionProps,
  DialogFooterProps,
  DialogHeaderProps,
  DialogOverlayProps,
  DialogRootProps,
  DialogTitleProps,
} from './dialog.types';

const DialogAnimatedElementContext = React.createContext<
  React.RefObject<HTMLElement | null> | undefined
>(undefined);

const Dialog = ({ onOpenChangeComplete, open, ...props }: DialogRootProps) => {
  const animatedElementRef = React.useRef<HTMLElement | null>(null);

  useOpenChangeComplete({
    onOpenChangeComplete,
    open,
    ref: animatedElementRef,
  });

  return (
    <DialogAnimatedElementContext.Provider value={animatedElementRef}>
      <DialogPrimitive.Root
        {...props}
        open={open}
        onOpenChange={(nextOpen) => props.onOpenChange?.(nextOpen)}
      />
    </DialogAnimatedElementContext.Provider>
  );
};
Dialog.displayName = 'Dialog';

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(dialogOverlayClasses, className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      overlayClassName,
      hideCloseIcon = false,
      onCloseIconClick,
      dismissible = true,
      portalContainer,
      preventFocusOutsideClose = false,
      closeIconClassName,
      ...props
    },
    ref
  ) => {
    const animatedElementRef = React.useContext(DialogAnimatedElementContext);
    const composedRef = useComposedRefs(ref, animatedElementRef);
    const intl = useDesignSystemIntl();
    const closeLabel = intl.formatMessage({
      defaultMessage: 'Close',
      id: '7RTpNjk/+u',
      description: 'Default accessible label for the Dialog built-in close button',
    });

    return (
      <DialogPortal container={portalContainer ?? undefined}>
        {!portalContainer ? (
          <DialogPrimitive.Overlay
            className={cn(dialogOverlayClasses, overlayClassName)}
          />
        ) : null}
        {/* When portalContainer is set, overlay is rendered by the parent (e.g. LibraryPage) */}
        <DialogPrimitive.Content
          onInteractOutside={dismissible ? undefined : (e) => e.preventDefault()}
          onPointerDownOutside={dismissible ? undefined : (e) => e.preventDefault()}
          onFocusOutside={
            !dismissible || preventFocusOutsideClose
              ? (e) => e.preventDefault()
              : undefined
          }
          ref={composedRef}
          className={cn(
            portalContainer
              ? dialogContentPositionAbsoluteClasses
              : dialogContentPositionFixedClasses,
            dialogContentClasses,
            className
          )}
          {...props}
        >
          {children}
          {!hideCloseIcon && (
            <DialogPrimitive.Close
              onClick={onCloseIconClick}
              className={cn(dialogCloseIconClasses, closeIconClassName)}
            >
              <X className="size-4" />
              <span className="sr-only">{closeLabel}</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: DialogHeaderProps) => (
  <div className={cn(dialogHeaderClasses, className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: DialogFooterProps) => (
  <div className={cn(dialogFooterClasses, className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  DialogTitleProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(dialogTitleClasses, className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  DialogDescriptionProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(dialogDescriptionClasses, className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
